/* ── Unified mobile drawer state (MENU, SHOP, BAG) ── */
import { lockScroll, unlockScroll } from '@theme/utilities';

(function () {
  var header = document.getElementById('header-component');
  var menuBg = header ? header.dataset.menuBg : '';
  var menuFg = header ? header.dataset.menuFg : '';
  var shopBg = header ? header.dataset.shopBg : '';
  var shopFg = header ? header.dataset.shopFg : '';

  /* bag_overlay_color_scheme lives on the _header-menu block's settings, but
     that block is declared `"static": true` in header-group.json -- Shopify
     excludes static blocks from `section.blocks` entirely (size 0, not just
     filtered from iteration), so there's no reliable Liquid-level way to read
     its settings from this section's own scope. Read the color straight off
     #bag-panel instead, which _header-menu.liquid already renders with the
     correct color-scheme class applied -- same source of truth the cart
     dialog's own color sync (below) already relies on. */
  var bagBg = '', bagFg = '';
  (function () {
    var bagPanel = document.getElementById('bag-panel');
    if (!bagPanel) return;
    var cs = getComputedStyle(bagPanel);
    var bg = cs.getPropertyValue('--color-background').trim();
    var fg = cs.getPropertyValue('--color-foreground').trim();
    if (bg) bagBg = bg;
    if (fg) bagFg = fg;
  })();
  var activeDrawer = null;

  /* Override the CSS body background (blue from :root first color scheme in color-schemes.liquid)
     with white at page load. This prevents iOS Safari from reading the blue body background for
     the bottom URL bar chrome — particularly visible at scroll-position 0 on the home page. */
  document.body.style.backgroundColor = '#ffffff';

  function applyState(isOpen, bg, fg) {
    var bagLink = document.querySelector('.header__mobile-bag-link');
    if (bagLink) {
      bagLink.childNodes.forEach(function (n) {
        if (n.nodeType === Node.TEXT_NODE) n.textContent = isOpen ? 'CLOSE' : 'BAG';
      });
      var count = document.getElementById('header-mobile-bag-count');
      if (count) count.style.display = isOpen ? 'none' : '';
    }

    /* NOTE: '' (clearing the inline style) is NOT the same as white here --
       .header declares `background: transparent` in CSS, so clearing the
       inline style falls back to a real, explicit transparent, not white.
       The header LOOKS white on close because white shows through from
       .header__underlay-closed/body beneath it, but the element's own
       background-color is transparent, not white. iOS Safari's status bar
       tracks this element's own declared color and doesn't reliably
       re-sample down to "transparent" -- it just keeps showing the last
       opaque color it saw (confirmed: scroll-nudging after clearing to ''
       still didn't reset it on-device). Using an explicit '#ffffff' on
       close instead gives iOS a real opaque color to switch to. */
    var headerSection = document.querySelector('.header-section');
    if (headerSection) {
      headerSection.classList.toggle('menu-is-open', isOpen);
      headerSection.style.backgroundColor = isOpen ? bg : '#ffffff';
    }

    var headerComponent = document.getElementById('header-component');
    if (headerComponent) headerComponent.style.backgroundColor = isOpen ? bg : '#ffffff';

    /* TEMP DIAGNOSTIC: bag is a native <dialog> in the browser's top layer,
       which fully occludes #header-component while open -- unlike MENU/SHOP,
       whose overlay is just very-high-z-index content in normal painting
       order, not a real top-layer promotion. Testing whether iOS falls back
       to sampling <body>'s own background specifically while a modal dialog
       is active (rather than whatever's occluded behind it), since body is
       otherwise hardcoded to white and never changes with drawer state. */
    document.body.style.backgroundColor = isOpen ? bg : '#ffffff';

    var headerRow = document.querySelector('.header__row--top');
    if (headerRow) {
      headerRow.style.backgroundColor = isOpen ? bg : '';
      headerRow.style.borderBottom = isOpen ? 'none' : '';
      headerRow.style.color = isOpen ? fg : '';
      var headerColumns = headerRow.querySelector('.header__columns');
      if (headerColumns) headerColumns.style.borderBottom = isOpen ? 'none' : '';
    }

    var underlayOpen = document.querySelector('.header__underlay-open');
    if (underlayOpen) underlayOpen.style.display = isOpen ? 'none' : '';

    /* .header__underlay-closed sits behind the header, right up to the iOS
       safe area/notch, and is left permanently white -- it and headerRow/
       headerSection/#header-component are the only elements near the top
       of the page iOS could plausibly be sampling for the status bar tint,
       and headerSection/#header-component turned out to be the actual
       mechanism (see comment above). */

    nudgeIOSChromeResample();
  }

  /* iOS Safari doesn't reliably re-sample the status bar/dynamic-island
     tint just because a background-color style changed -- this exact
     open/close toggle above was the original, pre-debugging behavior and
     still got stuck on close. The known reliable trigger for a resample
     is a genuine scroll event. Nudge the page 1px and back on the next
     two frames (giving scroll-lock a chance to release first) so iOS
     re-evaluates the chrome color against the now-updated background. */
  function nudgeIOSChromeResample() {
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        var y = window.scrollY;
        window.scrollTo(window.scrollX, y + 1);
        window.scrollTo(window.scrollX, y);
      });
    });
  }

  /* MENU/SHOP use <header-drawer>/<details scroll-lock>, which -- via the
     generic `toggle` listener in dialog.js -- also gets locked/unlocked
     through the shared utility. But that listener only fires once the
     `open` attribute is actually removed, which header-drawer.js delays
     until its close animation finishes (~200ms) so the native `toggle`
     event lines up with the iOS chrome-reset listener. That leaves the
     page scroll-locked and the header pinned for the full animation,
     then releasing both abruptly right as the drawer becomes invisible --
     visible as a content "jump" right after MENU/SHOP close (confirmed via
     Chrome: html[scroll-lock] and the header's position:fixed->sticky
     transition were both landing ~200ms after the click, not with it).
     Call lockScroll/unlockScroll directly here instead, synchronously with
     the click (same owner element dialog.js's listener would use), so the
     lock releases at the same time bag's does -- when the close animation
     starts, not when it ends. The later `toggle`-driven call becomes a
     harmless no-op since the owner's already gone from the lock Set. */
  function syncMenuShopScrollLock(name, isOpen) {
    if (name !== 'menu' && name !== 'shop') return;
    var ownerId = name === 'menu' ? 'Details-menu-drawer-container' : 'Details-shop-drawer-container';
    var owner = document.getElementById(ownerId);
    if (!owner) return;
    if (isOpen) lockScroll(owner);
    else unlockScroll(owner);
  }

  function setDrawer(name, isOpen, bg, fg) {
    if (isOpen) {
      /* Close whichever other drawer is currently open before opening this one */
      if (activeDrawer && activeDrawer !== name) {
        var prev = activeDrawer;
        activeDrawer = null; /* clear first so MutationObserver callback is a no-op */
        if (prev === 'menu') {
          var d = document.getElementById('Details-menu-drawer-container');
          if (d) { var hw = d.closest('header-drawer'); if (hw && hw.close) hw.close(); }
        } else if (prev === 'shop') {
          var d = document.getElementById('Details-shop-drawer-container');
          if (d) { var hw = d.closest('header-drawer'); if (hw && hw.close) hw.close(); }
        } else if (prev === 'bag') {
          /* cart-drawer-component has no .close() of its own -- dialog
             lifecycle belongs to the <theme-drawer> ancestor (see
             assets/cart-drawer.js's own doc comment). Calling .close() on
             the wrong element silently no-ops: activeDrawer still flips to
             null and the header still reverts, but the real drawer never
             closes, requiring a second click to actually dismiss it. */
          var cw = document.getElementById('cart-drawer');
          if (cw && cw.close) cw.close();
        }
        syncMenuShopScrollLock(prev, false);
      }
      activeDrawer = name;
      applyState(true, bg, fg);
      syncMenuShopScrollLock(name, true);
    } else if (activeDrawer === name) {
      activeDrawer = null;
      applyState(false, '', '');
      syncMenuShopScrollLock(name, false);
    }
  }

  function closeActiveDrawer() {
    var prev = activeDrawer;
    if (activeDrawer === 'menu') {
      var d = document.getElementById('Details-menu-drawer-container');
      if (d) { var hw = d.closest('header-drawer'); if (hw && hw.close) hw.close(); }
    } else if (activeDrawer === 'shop') {
      var d = document.getElementById('Details-shop-drawer-container');
      if (d) { var hw = d.closest('header-drawer'); if (hw && hw.close) hw.close(); }
    } else if (activeDrawer === 'bag') {
      /* See the matching comment in setDrawer() -- cart-drawer-component has
         no .close() of its own; the real dialog lifecycle lives on the
         <theme-drawer> ancestor. */
      var cw = document.getElementById('cart-drawer');
      if (cw && cw.close) cw.close();
    }
    activeDrawer = null;
    applyState(false, '', '');
    syncMenuShopScrollLock(prev, false);
  }

  function init() {
    var header = document.getElementById('header-component');
    if (!header) return;

    /* Watch MENU and SHOP header-drawer components */
    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var target = mutations[i].target;
        if (target.id === 'Details-menu-drawer-container') {
          var isOpen = target.classList.contains('menu-open');
          setDrawer('menu', isOpen, menuBg, menuFg);
          /* Do NOT clear target.open here — leave it for reset() to remove after
             the close animation, so the 'toggle' event fires at the correct time
             (after the animation) for our iOS chrome reset listener. */
          return;
        }
        if (target.id === 'Details-shop-drawer-container') {
          var isOpen = target.classList.contains('menu-open');
          setDrawer('shop', isOpen, shopBg, shopFg);
          /* Do NOT touch target.open here — for SHOP the component uses
             menu-open class (not details.open) as its state, and setting
             details.open triggers attributeChangedCallback which conflicts
             with the subsequent summary.click() open sequence. */
          return;
        }
      }
    }).observe(header, { attributes: true, attributeFilter: ['class'], subtree: true });

    /* Watch cart dialog open attribute for BAG state */
    var cartDialog = document.querySelector('#cart-drawer dialog');
    if (cartDialog) {
      new MutationObserver(function () {
        var isOpen = cartDialog.hasAttribute('open');
        setDrawer('bag', isOpen, bagBg, bagFg);
        /* Apply bag panel color scheme to the dialog directly.
           Read CSS custom properties from #bag-panel which already has the
           correct color-scheme class applied by _header-menu.liquid. */
        var bagPanel = document.getElementById('bag-panel');
        if (bagPanel) {
          var cs = getComputedStyle(bagPanel);
          var bg = cs.getPropertyValue('--color-background').trim();
          var fg = cs.getPropertyValue('--color-foreground').trim();
          if (bg) {
            cartDialog.style.setProperty('--color-background', isOpen ? bg : '');
            cartDialog.style.backgroundColor = isOpen ? bg : '';
          }
          if (fg) cartDialog.style.setProperty('--color-foreground', isOpen ? fg : '');
        }
      }).observe(cartDialog, { attributes: true, attributeFilter: ['open'] });
    }

    /* SHOP button: use close() to close, summary.click() to open.
       Do NOT pre-manipulate details.open — the component tracks state via
       menu-open class for non-trusted clicks, and touching details.open
       triggers attributeChangedCallback which breaks the open animation. */
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-toggle-drawer]');
      if (!btn) return;
      var details = document.getElementById(btn.dataset.toggleDrawer);
      if (!details) return;
      var drawer = details.closest('header-drawer');
      if (!drawer) return;
      if (details.classList.contains('menu-open')) {
        if (typeof drawer.close === 'function') drawer.close();
      } else {
        var summary = details.querySelector('summary');
        if (summary) summary.click();
      }
    });

    /* BAG/CLOSE button */
    document.addEventListener('click', function (e) {
      var link = e.target.closest('.header__mobile-bag-link');
      if (!link) return;
      e.preventDefault();
      if (activeDrawer) {
        closeActiveDrawer();
      } else {
        /* Unlike MENU/SHOP, the bag drawer is a native <dialog> opened via
           showModal(), and theme-drawer.js calls lockScroll() synchronously
           as part of that -- by the time our own MutationObserver notices
           the dialog opened and applyState() runs nudgeIOSChromeResample(),
           the body is already position:fixed and window.scrollTo() is a
           no-op. Do one real scroll nudge right here instead, still inside
           the trusted click and still genuinely scrollable, before handing
           off to the trigger that opens (and locks) the drawer. */
        window.scrollTo(window.scrollX, window.scrollY + 1);
        requestAnimationFrame(function () {
          window.scrollTo(window.scrollX, window.scrollY - 1);
        });
        var cartTrigger = document.querySelector('[data-testid="cart-drawer-trigger"]');
        if (cartTrigger) cartTrigger.click();
      }
    });

    /* Reset on resize to desktop */
    window.addEventListener('resize', function () {
      if (window.innerWidth >= 750) closeActiveDrawer();
    });

  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
